import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useClients } from '../context/ClientsContext';
import { useDocuments } from '../context/DocumentsContext';
import { User, Mail, Phone, Building, Edit, Trash, ArrowLeft, FileText, FilePlus, AlertTriangle } from 'lucide-react';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Input from '../components/UI/Input';

const ClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { clients, getClient, updateClient, deleteClient } = useClients();
  const { documents } = useDocuments();
  
  const client = getClient(id || '');
  
  const [isEditing, setIsEditing] = useState(false);
  const [editedClient, setEditedClient] = useState(client || {
    id: '',
    name: '',
    email: '',
    company: '',
    address: '',
    phone: '',
    siret: '',
    vat_number: '',
    createdAt: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  if (!client) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertTriangle className="h-12 w-12 text-warning-500 mb-4" />
        <h2 className="text-2xl font-bold">Client non trouvé</h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Le client que vous recherchez n'existe pas ou a été supprimé.
        </p>
        <Button 
          variant="outline" 
          className="mt-6"
          onClick={() => navigate('/app/clients')}
          leftIcon={<ArrowLeft className="h-4 w-4" />}
        >
          Retour aux clients
        </Button>
      </div>
    );
  }
  
  const clientDocuments = documents.filter(doc => doc.client_id === client.id);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditedClient(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };
  
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!editedClient.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }
    
    if (editedClient.email && !/\S+@\S+\.\S+/.test(editedClient.email)) {
      newErrors.email = "L'email n'est pas valide";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    setTimeout(() => {
      updateClient(client.id, editedClient);
      setIsEditing(false);
      setIsSubmitting(false);
    }, 500);
  };
  
  const handleDelete = () => {
    deleteClient(client.id);
    navigate('/app/clients');
  };
  
  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            className="mr-2"
            onClick={() => navigate('/app/clients')}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{client.name}</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {client.company || 'Client particulier'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {!isEditing && !showDeleteConfirm && (
            <>
              <Button 
                variant="outline"
                leftIcon={<Edit className="h-4 w-4" />}
                onClick={() => setIsEditing(true)}
              >
                Modifier
              </Button>
              <Button 
                variant="outline"
                className="text-error-500 border-error-300 hover:bg-error-50 dark:border-error-700 dark:hover:bg-error-900/20"
                leftIcon={<Trash className="h-4 w-4" />}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Supprimer
              </Button>
            </>
          )}
          
          {!isEditing && !showDeleteConfirm && (
            <Button 
              variant="primary"
              leftIcon={<FilePlus className="h-4 w-4" />}
              onClick={() => navigate('/app/create')}
            >
              Créer un document
            </Button>
          )}
        </div>
      </div>
      
      {showDeleteConfirm && (
        <Card className="border-error-300 dark:border-error-700">
          <Card.Content className="space-y-4">
            <div className="flex items-start">
              <div className="mr-4 p-2 rounded-full bg-error-100 dark:bg-error-900/20 text-error-500">
                <AlertTriangle className="h-6 w-6" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Supprimer le client</h3>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Êtes-vous sûr de vouloir supprimer {client.name} ? Cette action est irréversible.
                </p>
                {clientDocuments.length > 0 && (
                  <p className="text-error-500 mt-2 font-medium">
                    Attention : Ce client a {clientDocuments.length} document(s) associé(s).
                  </p>
                )}
              </div>
            </div>
          </Card.Content>
          
          <Card.Footer className="flex justify-end space-x-3">
            <Button 
              variant="outline" 
              onClick={() => setShowDeleteConfirm(false)}
            >
              Annuler
            </Button>
            <Button 
              variant="outline"
              className="text-error-500 border-error-300 hover:bg-error-50 dark:border-error-700 dark:hover:bg-error-900/20"
              onClick={handleDelete}
            >
              Supprimer
            </Button>
          </Card.Footer>
        </Card>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          {isEditing ? (
            <Card>
              <Card.Header title="Modifier les informations" />
              
              <form onSubmit={handleSubmit}>
                <Card.Content className="space-y-6">
                  <Input
                    label="Nom"
                    name="name"
                    value={editedClient.name}
                    onChange={handleInputChange}
                    required
                    error={errors.name}
                    leftIcon={<User className="h-4 w-4 text-gray-500 dark:text-gray-400" />}
                  />
                  
                  <Input
                    label="Email"
                    name="email"
                    type="email"
                    value={editedClient.email}
                    onChange={handleInputChange}
                    error={errors.email}
                    leftIcon={<Mail className="h-4 w-4 text-gray-500 dark:text-gray-400" />}
                  />
                  
                  <Input
                    label="Entreprise"
                    name="company"
                    value={editedClient.company}
                    onChange={handleInputChange}
                    leftIcon={<Building className="h-4 w-4 text-gray-500 dark:text-gray-400" />}
                  />
                  
                  <Input
                    label="Téléphone"
                    name="phone"
                    value={editedClient.phone}
                    onChange={handleInputChange}
                    leftIcon={<Phone className="h-4 w-4 text-gray-500 dark:text-gray-400" />}
                  />
                  
                  <Input
                    label="Adresse"
                    name="address"
                    value={editedClient.address}
                    onChange={handleInputChange}
                    leftIcon={<Building className="h-4 w-4 text-gray-500 dark:text-gray-400" />}
                  />

                  <Input
                    label="N° de SIRET"
                    name="siret"
                    value={editedClient.siret}
                    onChange={handleInputChange}
                  />

                  <Input
                    label="N° de TVA"
                    name="vat_number"
                    value={editedClient.vat_number}
                    onChange={handleInputChange}
                  />
                </Card.Content>
                
                <Card.Footer className="flex justify-end space-x-3">
                  <Button 
                    variant="outline" 
                    type="button" 
                    onClick={() => {
                      setIsEditing(false);
                      setEditedClient(client);
                      setErrors({});
                    }}
                  >
                    Annuler
                  </Button>
                  <Button 
                    type="submit" 
                    isLoading={isSubmitting}
                  >
                    {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
                  </Button>
                </Card.Footer>
              </form>
            </Card>
          ) : (
            <Card>
              <Card.Header title="Informations client" />
              <Card.Content className="space-y-4">
                <div className="flex items-center">
                  <User className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  <span className="ml-3">{client.name}</span>
                </div>
                
                {client.email && (
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    <a 
                      href={`mailto:${client.email}`} 
                      className="ml-3 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      {client.email}
                    </a>
                  </div>
                )}
                
                {client.company && (
                  <div className="flex items-center">
                    <Building className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    <span className="ml-3">{client.company}</span>
                  </div>
                )}
                
                {client.phone && (
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    <a 
                      href={`tel:${client.phone}`} 
                      className="ml-3 text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                    >
                      {client.phone}
                    </a>
                  </div>
                )}
                
                {client.address && (
                  <div className="flex items-start">
                    <Building className="h-5 w-5 text-gray-500 dark:text-gray-400 mt-0.5" />
                    <span className="ml-3">{client.address}</span>
                  </div>
                )}

                {client.siret && (
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    <span className="ml-3">SIRET : {client.siret}</span>
                  </div>
                )}

                {client.vat_number && (
                  <div className="flex items-center">
                    <FileText className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    <span className="ml-3">TVA : {client.vat_number}</span>
                  </div>
                )}
              </Card.Content>
              <Card.Footer>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Client depuis le {new Date(client.created_at).toLocaleDateString()}
                </p>
              </Card.Footer>
            </Card>
          )}
          
          <Card>
            <Card.Header 
              title="Statistiques client" 
              subtitle="Aperçu financier"
            />
            <Card.Content className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Documents totaux</span>
                <span className="font-medium">{clientDocuments.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Factures</span>
                <span className="font-medium">{clientDocuments.filter(doc => doc.type === 'invoice').length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Devis</span>
                <span className="font-medium">{clientDocuments.filter(doc => doc.type === 'quote').length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Montant total</span>
                <span className="font-medium">{clientDocuments.reduce((sum, doc) => sum + doc.amount, 0).toLocaleString()} €</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-400">Montant impayé</span>
                <span className="font-medium">{clientDocuments
                  .filter(doc => doc.type === 'invoice' && doc.status !== 'paid')
                  .reduce((sum, doc) => sum + doc.amount, 0)
                  .toLocaleString()} €</span>
              </div>
            </Card.Content>
          </Card>
        </div>
        
        <div className="lg:col-span-2">
          <Card>
            <Card.Header 
              title="Documents client" 
              action={
                <Button 
                  size="sm"
                  variant="outline"
                  leftIcon={<FilePlus className="h-4 w-4" />}
                  onClick={() => navigate('/app/create')}
                >
                  Nouveau document
                </Button>
              }
            />
            <Card.Content>
              {clientDocuments.length > 0 ? (
                <div className="space-y-4">
                  {clientDocuments
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                    .map(doc => (
                      <div 
                        key={doc.id}
                        className="flex items-center justify-between p-3 rounded-md border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                        onClick={() => navigate(`/app/documents/${doc.id}`)}
                      >
                        <div className="flex items-center">
                          <div className={`
                            p-2 rounded-md mr-3
                            ${doc.type === 'invoice' 
                              ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' 
                              : 'bg-secondary-50 dark:bg-secondary-900/20 text-secondary-600 dark:text-secondary-400'
                            }
                          `}>
                            <FileText className="h-5 w-5" />
                          </div>
                          <div>
                            <h4 className="font-medium">{doc.type === 'invoice' ? 'Facture' : 'Devis'} #{doc.number}</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {doc.amount.toLocaleString()} € - {new Date(doc.date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center">
                          <div
                            className={`
                              text-xs font-medium px-2 py-1 rounded-full
                              ${doc.status === 'paid' 
                                ? 'bg-green-50 dark:bg-green-900/20 text-success-500' 
                                : doc.status === 'sent' 
                                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                                  : doc.status === 'cancelled'
                                    ? 'bg-red-50 dark:bg-red-900/20 text-error-500'
                                    : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                              }
                            `}
                          >
                            {doc.status === 'paid' ? 'Payé' : 
                             doc.status === 'sent' ? 'Envoyé' : 
                             doc.status === 'draft' ? 'Brouillon' : 
                             'Annulé'}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="py-12 flex flex-col items-center justify-center text-center">
                  <div className="h-16 w-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 mb-4">
                    <FileText className="h-8 w-8" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">Aucun document</h3>
                  <p className="mt-1 text-gray-500 dark:text-gray-400">
                    Créez votre premier document pour ce client.
                  </p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    leftIcon={<FilePlus className="h-4 w-4" />}
                    onClick={() => navigate('/app/create')}
                  >
                    Créer un document
                  </Button>
                </div>
              )}
            </Card.Content>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;